/* eslint-disable no-prototype-builtins */
import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import store from '@/store';
import checkout from '@/store/modules/checkout';
import handleError from '@/commons/errorHandler';
import { success } from '@/commons/toast';
import to from 'await-to-js';
import {
    getHipayPayment,
    klarnaUpdateSession,
    klarnaWaitAuthCallback,
    resetReservedOrderId,
    saveLog
} from '@/store/api';
import { bus } from '@/events/bus';
import paidEvent from '@/events/paid';
import { i18n } from '@/i18n';
import { PayResult } from '@/store/models';
import redirectToLocation from '@/commons/redirectManager';
import loadScript from '@/commons/scriptLoad';
import handleMessage from '@/commons/messageManager';
import JQuery from 'jquery';
import stripe from '@/store/modules/stripe';
import { initState } from '../stateinit';
import ui from './ui';
import PayPalAdapter from './payment/paypalAdapter';
import cart from './cart';

function methodCodeTranslator(methodCode: string) {
    const map: any = {
        wf_cashondelivery: 'cod',
        banktransfer: 'banktransfer',
        wf_paypal: 'paypal',
        wf_paypalpaylater: 'paypal',
        hipay: 'hipay',
        free: 'free',
        hipay_oney: 'hipay_oney',
        scalapay: 'scalapay',
        payin4: 'scalapay_payin4',
        hipay_paybylink: 'hipay_paybylink',
        hipay_moto: 'hipay_moto',
        hipay_bancontact: 'hipay_redirect',
        hipay_sofort: 'hipay_redirect',
        stripe_payments: 'stripe_payments',
        stripe_payments_moto: 'stripe_payments_moto',
    };

    return map.hasOwnProperty(methodCode) ? map[methodCode] : methodCode;
}

function methodCodeForceInit(methodCodeTranslated: string) {
    const map: any = {
        paypal: true
    };

    return map.hasOwnProperty(methodCodeTranslated) ? map[methodCodeTranslated] : false;
}

const hipayCardApi: any = {
    hipayInstance: undefined,
    cardInstance: undefined,
    createHipayInstance: (hipayConfig: any) => {
        return new Promise((resolve) => {
            if (window.HiPay !== undefined) {
                hipayCardApi.instanceHiPay(hipayConfig);
            } else {
                loadScript(hipayConfig.sdk_url, true, false).then(() => {
                    hipayCardApi.instanceHiPay(hipayConfig);
                });
            }
            resolve(true);
        });
    },
    instanceHiPay: (hipayConfig: any) => {
        if (!hipayCardApi.hipayInstance) {
            hipayCardApi.hipayInstance = new window.HiPay({
                username: hipayConfig.username,
                password: hipayConfig.password,
                environment: hipayConfig.env,
                lang: hipayConfig.lang
            });
        }
        if (!hipayCardApi.cardInstance) {
            hipayCardApi.cardInstance = hipayCardApi.hipayInstance.create('card', {
                template: 'auto',
                selector: 'hipayhostedfields'
            });
        }
    },
    tokenize: (cardTokenization: any) => {
        return new Promise((resolve, reject) => {
            hipayCardApi.cardInstance.setMultiUse(cardTokenization.tokenize_card);
            hipayCardApi.cardInstance.getPaymentData().then(
                (res: any) => {
                    resolve({
                        brand: res.brand,
                        multi_use: res.multi_use,
                        token: res.token
                    });
                },
                (errs: any) => {
                    checkout.flagPlacingOrder(false);
                    reject(errs.map((e: any) => e.error).join('\n'));
                }
            );
        });
    }
};

function waitAuth(authorizationToken: string) {
    return new Promise((resolve) => {
        klarnaWaitAuthCallback(authorizationToken)
            .then((res) => {
                resolve(res);
            })
            .catch((e) => {
                checkout.flagPlacingOrder(false);
                resolve(e.response.data);
            });
    });
}

const hipayApplePayApi: any = {
    hipayInstance: undefined,
    applePayInstance: undefined,
    getGrandTotal: () => {
        const grandTotal = cart.totals.total_segments.find((total) => {
            return total.code === 'grand_total';
        });

        return grandTotal ? grandTotal.value.toString() : '0';
    },
    createHipayInstance: (hipayApplePayConfig: any) => {
        return new Promise((resolve) => {
            if (window.HiPay !== undefined) {
                hipayApplePayApi.instanceHiPay(hipayApplePayConfig);
            }
            loadScript(hipayApplePayConfig.sdk_url, false, false).then(() => {
                hipayApplePayApi.instanceHiPay(hipayApplePayConfig);
            });
            resolve(true);
        });
    },
    instanceHiPay: (hipayApplePayConfig: any) => {
        if (!hipayApplePayApi.hipayInstance) {
            hipayApplePayApi.hipayInstance = new window.HiPay({
                username: hipayApplePayConfig.username,
                password: hipayApplePayConfig.password,
                environment: hipayApplePayConfig.env,
                lang: hipayApplePayConfig.lang
            });
        }
        if (!hipayApplePayApi.applePayInstance) {
            // Configuration
            const total = {
                label: 'Total',
                amount: hipayApplePayApi.getGrandTotal()
            };

            const request = {
                countryCode: checkout.shippingAddress.country_id,
                currencyCode: store.state.options.currency.code,
                total,
                supportedNetworks: hipayApplePayConfig.product_list.split(',')
            };

            const applePayStyle = {
                type: 'plain',
                color: 'black'
            };

            const options = {
                displayName: hipayApplePayConfig.display_name,
                request,
                applePayStyle,
                selector: 'apple-pay-button'
            };

            hipayApplePayApi.applePayInstance = hipayApplePayApi.hipayInstance.create(
                'paymentRequestButton',
                options
            );

            hipayApplePayApi.hipayInstance
                .canMakePaymentsWithActiveCard(hipayApplePayConfig.merchant_id)
                .then((canMakePayments: any) => {
                    checkout.setCanMakeApplePayPayments(canMakePayments);
                    if (canMakePayments) {
                        if (hipayApplePayApi.applePayInstance) {
                            // Apple Pay button displayed
                            hipayApplePayApi.applePayInstance.on('paymentAuthorized', (result: any) => {
                                hipayApplePayApi.applePayInstance.completePaymentWithSuccess();

                                const payData: any = {};
                                payData.checkoutMethod = 'placeAndCreateOrder';

                                const cardData: any = {};
                                cardData.brand = result.brand;
                                cardData.token = result.token;

                                bus.$emit(paidEvent, {
                                    payData: {
                                        extension_attributes: {
                                            hipay_hostedfields_tokenization: cardData
                                        },
                                        checkoutMethod: 'placeAndCreateOrder',
                                        placeCallback: () => {
                                            redirectToLocation('/checkout/onepage/success');
                                        }
                                    },
                                    paymentMethod: checkout.selectedPaymentMethod.code
                                });
                            });
                            hipayApplePayApi.applePayInstance.on('cancel', () => {
                                // TODO: add error catching
                            });
                            hipayApplePayApi.applePayInstance.on('paymentUnauthorized', (error: any) => {
                                console.log(error);
                                checkout.flagPlacingOrder(false);
                                handleMessage('error', i18n.t('Error occurred while placing order').toString());
                                window.location.reload();
                            });
                        } else {
                            checkout.setCanMakeApplePayPayments(false);
                            JQuery('#apple-pay-button').hide();
                        }
                    } else {
                        checkout.setCanMakeApplePayPayments(false);
                        JQuery('#apple-pay-button').hide();
                    }
                });
        }
    }
};

const adapterClasses: any = {
    paypal: new PayPalAdapter()
};

const adapters = {
    teardown_generic: () => {
        return new Promise((resolve) => {
            resolve({});
        });
    },
    teardown_hipay: () => {
        return new Promise((resolve) => {
            if (hipayCardApi.cardInstance) {
                hipayCardApi.cardInstance.destroy();
                hipayCardApi.cardInstance = undefined;
            }
            resolve({});
        });
    },
    teardown_applepay: () => {
        return new Promise((resolve) => {
            if (hipayApplePayApi.applePayInstance) {
                hipayApplePayApi.applePayInstance.destroy();
                hipayApplePayApi.applePayInstance = undefined;
            }
            resolve({});
        });
    },
    init_generic: () => {
        return new Promise((resolve) => {
            resolve({});
        });
    },
    pay_generic: (amount: number) => {
        return new Promise((resolve) => {
            resolve({ capturedAmount: amount });
        });
    },

    init_hipay: (params: any) => {
        return new Promise((resolve, reject) => {
            const placeImmediate = params.payment_action === 'order';

            if (params.use_hostedfields && params.mode !== 'paypal') {
                hipayCardApi.createHipayInstance(params).then(() => {
                    resolve(hipayCardApi);
                });
            } else if (placeImmediate) {
                resolve({ placeImmediate });
            } else {
                getHipayPayment(params.mode)
                    .then((res) => {
                        resolve({
                            iframesrc: res
                        });
                    })
                    .catch((e) => {
                        if (params.tryrestorepayment && e.response.data && e.response.data.code === 87) {
                            bus.$emit(paidEvent, {
                                useStoredData: true,
                                payData: {},
                                paymentMethod: store.state.options.payment.hipay.code,
                                redirectUrl: '/checkout/onepage/success'
                            });

                            const mess = i18n
                                .t('The order is already been paid, please wait while order is placing')
                                .toString();
                            success(mess);
                            reject(mess);
                        } else {
                            resetReservedOrderId().then(() => {
                                const mess = i18n.t('Service unavailable, retry later').toString();
                                resolve({
                                    error: mess
                                });
                                redirectToLocation('/checkout/cart');
                            });
                        }
                    });
            }
        });
    },

    init_applepay: (params: any) => {
        return new Promise((resolve) => {
            hipayApplePayApi.createHipayInstance(params).then(() => {
                resolve(hipayApplePayApi);
            });
        });
    },

    init_klarna: (params: any) => {
        return new Promise((resolve) => {
            window.klarnaAsyncCallback = () => {
                Klarna.Payments.init({ client_token: params.client_token });
            };
            const sdkUrl = 'https://x.klarnacdn.net/kp/lib/v1/api.js';
            loadScript(sdkUrl, true, false).then(() => {
                resolve({});
            });
        });
    },

    pay_hipay_iframe: (amount: number, params: any) => {
        return new Promise((resolve) => {
            const payData: any = {};

            const methodCode = checkout.selectedPaymentMethod.code;
            const hipayConfig = store.state.options.payment.hipay;
            const cardTokenization = params.paymentInstance.additionalData.hipay_tokenization;
            if (methodCode === 'hipay' && hipayConfig.use_hostedfields) {
                payData.extension_attributes = {
                    hipay_should_tokenize_card: cardTokenization.tokenize_card,
                    hipay_card_token_id: cardTokenization.tokenId,
                    hipay_hostedfields_tokenization: cardTokenization.hostedfields_tokenization
                };
                payData.checkoutMethod = 'placeAndCreateOrder';
                payData.placeCallback = (orderData: any) => {
                    if (orderData.extension_attributes && orderData.extension_attributes.redirect_url) {
                        window.location.replace(orderData.extension_attributes.redirect_url);
                    } else {
                        checkout.flagPlacingOrder(false);
                        handleMessage('error', i18n.t('Error occurred while placing order').toString());
                    }
                };
            } else if (params.placeImmediate) {
                payData.checkoutMethod = 'placeAndCreateOrder';
                payData.placeCallback = (orderData: any) => {
                    if (orderData.extension_attributes && orderData.extension_attributes.redirect_url) {
                        const $cont = document.getElementById('hipay-payment-form');

                        if ($cont && $cont.children && $cont.children.length) {
                            $cont.removeChild($cont.children[0]);
                        }

                        const iframe = document.createElement('iframe');
                        iframe.src = orderData.extension_attributes.redirect_url;

                        if ($cont) {
                            $cont.appendChild(iframe);
                        }

                        ui.openModal('hipay');
                    } else {
                        checkout.flagPlacingOrder(false);
                        handleMessage('error', i18n.t('Error occurred while placing order').toString());
                    }
                };
            }
            resolve(payData);
        });
    },
    pay_hipay_redirect: (amount: number, params: any) => {
        return new Promise((resolve) => {
            const payData: any = {};

            if (params.placeImmediate) {
                payData.checkoutMethod = 'placeAndCreateOrder';
                payData.placeCallback = (orderData: any) => {
                    if (orderData.extension_attributes && orderData.extension_attributes.redirect_url) {
                        window.location.replace(orderData.extension_attributes.redirect_url);
                    } else {
                        handleMessage('error', i18n.t('Error occurred while placing order').toString());
                    }
                };
            }
            resolve(payData);
        });
    },
    pay_stripe_iframe: (amount: number, params: any) => {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
            const payData: any = {};
            const cardTokenization = false; // TODO

            const stripeElements = await stripe.getStripeElements();

            if (stripe.stripe && stripeElements) {
                const { error: submitError } = await stripeElements.submit();

                if (submitError) {
                    checkout.flagPlacingOrder(false);
                    reject(i18n.t('Error during payment authorization').toString());
                }

                const { confirmationToken, error: confirmationTokenError } =
                    await stripe.stripe!.createConfirmationToken({
                        elements: stripeElements!,
                    });

                if (confirmationTokenError) {
                    checkout.flagPlacingOrder(false);
                    reject(i18n.t('Error during payment authorization').toString());
                }

                payData.additional_data = {
                    token: confirmationToken ? confirmationToken.id : '',
                    confirmation_token: confirmationToken ? confirmationToken.id : '',
                    stripe_payment_method: stripe.selected_method_code,
                };
                payData.checkoutMethod = 'placeAndCreateOrder';
                payData.placeCallback = async (orderData: any) => {
                    if (
                        orderData.extension_attributes &&
                        orderData.extension_attributes.stripe_is_transaction_pending &&
                        orderData.extension_attributes.stripe_client_secret
                    ) {
                        if (stripe.stripe) {
                            const { paymentIntent, error: paymentIntentError } =
                                await stripe.stripe.retrievePaymentIntent(
                                    orderData.extension_attributes.stripe_client_secret
                                );

                            if (paymentIntentError) {
                                checkout.flagPlacingOrder(false);
                                handleMessage('error', i18n.t('Error during payment redirection').toString());
                                window.location.reload();
                            }

                            checkout.flagPlacingOrder(false);
                            if (paymentIntent!.status == 'requires_action') {
                                // If we already have a url, then redirect, else let Stripe handle the next actions
                                if (paymentIntent && paymentIntent.next_action && paymentIntent.next_action.redirect_to_url) {
                                    window.location.replace(paymentIntent!.next_action!.redirect_to_url!.url || '');
                                } else {
                                    const result = await stripe.stripe.handleNextAction({
                                        clientSecret: orderData.extension_attributes.stripe_client_secret
                                    });

                                    checkout.flagPlacingOrder(false);
                                    if (result.error) {
                                        handleMessage('error', result.error!.message || i18n.t('Error during payment next action').toString());
                                        window.location.reload();
                                    } else {
                                        redirectToLocation('/checkout/onepage/success');
                                    }
                                }
                            }
                        }
                    } else {
                        checkout.flagPlacingOrder(false);
                        redirectToLocation('/checkout/onepage/success');
                    }
                };

                resolve(payData);
            } else {
                reject(i18n.t('Error during payment authorization').toString());
            }
        });
    },
    klarna_authorization: (amount: number, params: any, methodCode: string) => {
        const category = methodCode.replace('klarna_', '');
        return new Promise((resolve, reject) => {
            const { billingAddress } = checkout;
            const { shippingAddress } = checkout;
            const klarnaShipping = {
                given_name: shippingAddress.firstname,
                family_name: shippingAddress.lastname,
                email: checkout.email,
                title: '',
                street_address: shippingAddress.street[0],
                postal_code: shippingAddress.postcode,
                city: shippingAddress.city,
                region: '',
                phone: shippingAddress.telephone,
                country: shippingAddress.country_id
            };
            const klarnaBilling = {
                given_name: billingAddress.firstname,
                family_name: billingAddress.lastname,
                email: checkout.email,
                title: '',
                street_address: billingAddress.street[0],
                postal_code: billingAddress.postcode,
                city: billingAddress.city,
                region: '',
                phone: billingAddress.telephone,
                country: billingAddress.country_id
            };

            Klarna.Payments.authorize(
                {
                    payment_method_category: category
                },
                {
                    billing_address: klarnaBilling,
                    shipping_address: klarnaShipping
                },
                (res: any) => {
                    if (res.approved === true) {
                        const authorizationToken = res.authorization_token;

                        waitAuth(authorizationToken).then((klarnaGetAuthRes: any) => {
                            const orderId = klarnaGetAuthRes.order_id;
                            const orderIncrement = klarnaGetAuthRes.order_increment;
                            checkout.setOrderId(orderIncrement);

                            if (klarnaGetAuthRes.is_active === '1' || !orderId) {
                                // old flow
                                const payData: any = {
                                    additional_data: {
                                        method_title: checkout.selectedPaymentMethod.title,
                                        authorization_token: authorizationToken
                                    }
                                };

                                resolve({ payData });
                            } else {
                                // order is place in authorize webhook
                                bus.$emit(paidEvent, {
                                    payData: {
                                        checkoutMethod: 'orderAlreadyPlaced',
                                        placeCallback: () => {
                                            klarnaUpdateSession(authorizationToken)
                                                .then(() => {
                                                    checkout.flagOrderPlaced(false);
                                                    handleMessage('success', i18n.t('Order placed').toString());
                                                    redirectToLocation('/checkout/onepage/success');
                                                })
                                                .catch(() => {
                                                    checkout.flagPlacingOrder(false);
                                                    handleMessage(
                                                        'error',
                                                        i18n.t('Error during payment authorization').toString()
                                                    );
                                                });
                                        }
                                    },
                                    paymentMethod: checkout.selectedPaymentMethod.code
                                });
                            }
                        });
                    } else {
                        reject(i18n.t('Error during payment authorization').toString());
                    }
                }
            );
        });
    }
};

// called in pay action
const payActions: any = {
    banktransfer: adapters.pay_generic,
    cod: adapters.pay_generic,
    free: adapters.pay_generic,
    scalapay: adapters.pay_generic,
    scalapay_payin4: adapters.pay_generic,
    hipay: adapters.pay_hipay_iframe,
    hipay_oney: adapters.pay_hipay_redirect,
    hipay_paybylink: adapters.pay_hipay_redirect,
    hipay_moto: adapters.pay_hipay_iframe,
    hipay_redirect: adapters.pay_hipay_redirect,
    hipay_applepay: adapters.pay_generic,
    stripe_payments: adapters.pay_stripe_iframe,
    stripe_payments_moto: adapters.pay_generic,
};

// called in registration action
const methodIniter: any = {
    banktransfer: adapters.init_generic,
    cod: adapters.init_generic,
    free: adapters.init_generic,
    scalapay: adapters.init_generic,
    scalapay_payin4: adapters.init_generic,
    hipay_paybylink: adapters.init_generic,
    hipay: adapters.init_hipay,
    hipay_redirect: adapters.init_hipay,
    hipay_oney: adapters.init_hipay,
    hipay_moto: adapters.init_hipay,
    hipay_applepay: adapters.init_applepay,
    stripe_payments: adapters.init_generic,
    stripe_payments_moto: adapters.init_generic,
};

const methodTeardown: any = {
    paypal: adapters.teardown_generic,
    hipay: adapters.teardown_hipay,
    hipay_oney: adapters.teardown_generic,
    scalapay: adapters.teardown_generic,
    hipay_redirect: adapters.teardown_generic,
    scalapay_payin4: adapters.teardown_generic,
    hipay_paybylink: adapters.teardown_generic,
    hipay_moto: adapters.teardown_generic,
    hipay_applepay: adapters.teardown_applepay,
    stripe_payments: adapters.teardown_generic,
    stripe_payments_moto: adapters.teardown_generic,
};

@Module({
    namespaced: true,
    name: 'payment',
    store,
    dynamic: true
})
class PaymentModule extends VuexModule {
    public registry: string[] = [];
    public methodData: any[] = [];
    public salePayload: { success: boolean; data: any } = { success: false, data: {} };
    public paymentSuccessInfo: any = initState('paymentSuccessInfo');
    public additionalData: any = {
        paypal_tokenization: {
            save_in_vault: false,
            vaulted_id: 0,
            vaulted_email: ''
        },
        hipay_tokenization: {
            hostedfields_tokenization: {},
            tokenize_card: false,
            tokenId: 0
        }
    };

    get dataByMethod(): any {
        return (methodCode: string) => {
            // eslint-disable-next-line no-param-reassign
            methodCode = methodCodeTranslator(methodCode);
            const method = this.methodData.find((m: any) => m.methodCode === methodCode);
            if (method && method.data) {
                method.data.paymentInstance = this;
            }

            return method ? method.data : {};
        };
    }

    @Mutation
    public register(methodCode: string) {
        this.registry.push(methodCode);
    }

    @Mutation
    public unregister(methodCode: string) {
        const mc = methodCodeTranslator(methodCode);
        const idx = this.registry.findIndex((m) => m === mc);

        if (idx !== -1) {
            this.registry.splice(idx, 1);
        }
    }

    @Mutation
    public addMethodData(payload: { methodCode: string; data: any }) {
        this.methodData.push({ methodCode: payload.methodCode, data: payload.data });
    }

    @Mutation
    public unsetMethodData(methodCode: string) {
        this.methodData.forEach((m: any, idx) => {
            if (m.methodCode === methodCode) {
                this.methodData.splice(idx, 1);
            }
        });
    }

    @Mutation
    public sale(payload: { success: boolean; data: any }) {
        this.salePayload = payload;
    }

    @Action({ rawError: true })
    public async registerMethod(payload: { methodCode: string; params?: any }): Promise<any> {
        let error: any;
        let registrationRes: any;
        const methodCode = methodCodeTranslator(payload.methodCode);
        const forceInit = methodCodeForceInit(methodCode);

        if (forceInit) {
            this.unregister(methodCode);
        }

        if (!this.registry.includes(methodCode)) {
            this.register(methodCode);
            const params = payload.params || {};
            params.saleCommit = this.sale;
            if (adapterClasses.hasOwnProperty(methodCode)) {
                [error, registrationRes] = await to(adapterClasses[methodCode].init(params));
            } else {
                [error, registrationRes] = await to(methodIniter[methodCode](params));
            }

            return new Promise((resolve) => {
                if (error) {
                    // eslint-disable-next-line no-console
                    console.log(error);
                    handleError(error);
                    if (error && error.stack && error.message) {
                        saveLog(`RegisterPayment ${methodCode}. ${error.message}. Stack ${error.stack}`);
                    }
                    resolve({
                        paid: false
                    });
                } else {
                    if (registrationRes.error) {
                        // eslint-disable-next-line no-console
                        console.log(error);
                        handleError(registrationRes.error);
                        resolve({
                            paid: false,
                            error: registrationRes.error
                        });
                    }

                    this.addMethodData({ methodCode, data: registrationRes });
                    resolve(true);
                }
            });
        }
        return new Promise((resolve) => {
            resolve(true);
        });
    }

    @Action({ rawError: true })
    public async unregisterMethod(method: string): Promise<any> {
        let error: any;
        const methodCode = methodCodeTranslator(method);

        if (this.registry.includes(methodCode)) {
            this.unregister(methodCode);
            [error] = await to(methodTeardown[methodCode](this.dataByMethod(methodCode)));
            return new Promise((resolve, reject) => {
                if (error) {
                    // eslint-disable-next-line no-console
                    console.log(error);
                    handleError(error);
                    if (error && error.stack && error.message) {
                        saveLog(`UnregisterPayment ${methodCode}. ${error.message}. Stack ${error.stack}`);
                    }
                    // eslint-disable-next-line prefer-promise-reject-errors
                    reject(false);
                } else {
                    this.unsetMethodData(methodCode);
                    resolve(true);
                }
            });
        }
        return new Promise((resolve) => {
            resolve(true);
        });
    }

    @Action({ rawError: true })
    public async pay(payload: { methodCode: string; params?: any; amount: number }): Promise<PayResult> {
        const params = payload.params || {};
        const methodCode = methodCodeTranslator(payload.methodCode);
        let error: any;
        let payRes: any;

        if (payActions.hasOwnProperty(methodCode)) {
            [error, payRes] = await to(payActions[methodCode](payload.amount, params, methodCode));
        } else {
            error = i18n.t('Payment method not supported').toString();
        }

        return new Promise((resolve) => {
            let res: PayResult;

            if (error) {
                res = { success: false, data: null, error: error as string };
            } else {
                res = { success: true, data: payRes, error: '' };
            }

            this.sale(res);
            resolve(res);
        });
    }
}

export default getModule(PaymentModule);
